import React, { useState, useEffect } from 'react';
import { Sparkles, Wand2, Palette, BookOpen, Music, Check, Loader2 } from './Icons';

export interface ProgressState {
  stage: 'story' | 'assets';
  completed: number;
  total: number;
  startTime: number;
}

interface StoryCreationProgressProps {
  isLoading: boolean;
  progressState?: ProgressState;
}

const StoryCreationProgress: React.FC<StoryCreationProgressProps> = ({ isLoading, progressState }) => {
  const [visible, setVisible] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  useEffect(() => {
    if (isLoading) {
      setVisible(true);
      setElapsedSeconds(0);
    } else {
      const timer = window.setTimeout(() => setVisible(false), 800);
      return () => clearTimeout(timer);
    }
  }, [isLoading]);

  // Track elapsed time
  useEffect(() => {
    if (!isLoading || !progressState?.startTime) return;

    const interval = window.setInterval(() => {
      setElapsedSeconds(Math.floor((Date.now() - progressState.startTime) / 1000));
    }, 1000);

    return () => clearInterval(interval);
  }, [isLoading, progressState?.startTime]);

  // Wake Lock: keep screen on during generation
  useEffect(() => {
    if (!isLoading) return;
    if (!('wakeLock' in navigator)) return;

    let wakeLock: WakeLockSentinel | null = null;

    const acquire = async () => {
      try {
        wakeLock = await (navigator as any).wakeLock.request('screen');
      } catch {
        // Wake Lock denied (low battery, etc.) — silent fail, generation continues
      }
    };

    // Re-acquire if the page becomes visible again (e.g. user switched tabs)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') acquire();
    };

    acquire();
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      wakeLock?.release().catch(() => {});
    };
  }, [isLoading]);

  if (!visible) return null;

  // Calculate real progress percentage
  const isStoryPhase = !progressState || progressState.stage === 'story';
  const storyComplete = progressState?.stage === 'assets';
  
  // Progress calculation: story phase = 0-20%, assets phase = 20-100%
  let progressPercent = 5; // minimum
  if (progressState) {
    if (progressState.stage === 'story') {
      progressPercent = 10; // Story is generating
    } else if (progressState.stage === 'assets') {
      const assetProgress = progressState.total > 0 
        ? progressState.completed / progressState.total 
        : 0;
      progressPercent = 20 + (assetProgress * 80); // 20% to 100%
    }
  }
  
  // Determine which assets are being created
  const totalAssets = progressState?.total || 0;
  const completedAssets = progressState?.completed || 0;
  
  // Assets breakdown: 1 cover + (pages * 2) for image + audio
  // We'll estimate: first asset is cover image, then alternating image/audio for pages
  const pageCount = totalAssets > 0 ? Math.floor((totalAssets - 1) / 2) : 0;
  const imagesTotal = pageCount + 1; // cover + page images
  const audioTotal = pageCount;
  
  // Estimate images vs audio completed (rough - they run in parallel)
  const imagesCompleted = Math.min(imagesTotal, Math.ceil(completedAssets * 0.6));
  const audioCompleted = Math.max(0, completedAssets - imagesCompleted);
  
  const allImagesComplete = storyComplete && imagesCompleted >= imagesTotal;
  const allAudioComplete = storyComplete && audioCompleted >= audioTotal;

  const formatTime = (seconds: number) => {
    if (seconds < 60) return `${seconds}s`;
    return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
  };

  return (
    <div className={`fixed inset-0 z-[250] flex items-center justify-center p-6 bg-[#0f0e17]/98 backdrop-blur-2xl transition-opacity duration-500 ${isLoading ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
      <div className="max-w-md w-full space-y-8 text-center animate-fade-in">
        
        {/* Animated Icon */}
        <div className="relative flex justify-center">
          <div className="absolute inset-0 bg-gold-500/30 blur-[80px] rounded-full animate-pulse"></div>
          <div className="relative w-24 h-24 bg-white/5 border border-white/10 rounded-[2rem] flex items-center justify-center shadow-2xl animate-float">
            <Wand2 className="w-12 h-12 text-gold-400" />
            <Sparkles className="absolute -top-2 -right-2 w-6 h-6 text-yellow-300 animate-pulse" />
          </div>
        </div>

        {/* Title */}
        <h2 className="text-2xl md:text-3xl font-serif font-bold text-white tracking-tight">
          Forging Your Adventure
        </h2>

        {/* Stage Timeline */}
        <div className="bg-white/5 border border-white/10 rounded-2xl p-5 space-y-3 text-left">
          {/* Stage 1: Writing Story */}
          <div className="flex items-center gap-3">
            <div className={`w-7 h-7 rounded-full flex items-center justify-center transition-all duration-500 ${
              storyComplete 
                ? 'bg-green-500/20 border-2 border-green-500' 
                : 'bg-gold-500/20 border-2 border-gold-500'
            }`}>
              {storyComplete ? (
                <Check className="w-4 h-4 text-green-400" />
              ) : (
                <Loader2 className="w-4 h-4 text-gold-400 animate-spin" />
              )}
            </div>
            <div className="flex-1">
              <span className={`font-medium ${storyComplete ? 'text-green-400' : 'text-white'}`}>
                Writing Story
              </span>
            </div>
            {storyComplete && <span className="text-green-400 text-xs font-bold">✓ Complete</span>}
          </div>

          {/* Stage 2: Creating Illustrations */}
          <div className="flex items-center gap-3">
            <div className={`w-7 h-7 rounded-full flex items-center justify-center transition-all duration-500 ${
              allImagesComplete 
                ? 'bg-green-500/20 border-2 border-green-500'
                : storyComplete
                  ? 'bg-forge-500/20 border-2 border-forge-500'
                  : 'bg-white/5 border-2 border-white/20'
            }`}>
              {allImagesComplete ? (
                <Check className="w-4 h-4 text-green-400" />
              ) : storyComplete ? (
                <Palette className="w-4 h-4 text-forge-400 animate-pulse" />
              ) : (
                <Palette className="w-4 h-4 text-white/30" />
              )}
            </div>
            <div className="flex-1">
              <span className={`font-medium ${
                allImagesComplete ? 'text-green-400' : storyComplete ? 'text-white' : 'text-white/40'
              }`}>
                Creating Illustrations
              </span>
            </div>
            {storyComplete && !allImagesComplete && imagesTotal > 0 && (
              <span className="text-forge-400 text-xs font-bold tabular-nums">
                {Math.min(imagesCompleted, imagesTotal)}/{imagesTotal}
              </span>
            )}
            {allImagesComplete && <span className="text-green-400 text-xs font-bold">✓ Complete</span>}
          </div>

          {/* Stage 3: Preparing Audio */}
          <div className="flex items-center gap-3">
            <div className={`w-7 h-7 rounded-full flex items-center justify-center transition-all duration-500 ${
              allAudioComplete 
                ? 'bg-green-500/20 border-2 border-green-500'
                : storyComplete && imagesCompleted > 0
                  ? 'bg-spark-500/20 border-2 border-spark-500'
                  : 'bg-white/5 border-2 border-white/20'
            }`}>
              {allAudioComplete ? (
                <Check className="w-4 h-4 text-green-400" />
              ) : storyComplete && imagesCompleted > 0 ? (
                <Music className="w-4 h-4 text-spark-400 animate-pulse" />
              ) : (
                <Music className="w-4 h-4 text-white/30" />
              )}
            </div>
            <div className="flex-1">
              <span className={`font-medium ${
                allAudioComplete ? 'text-green-400' : (storyComplete && imagesCompleted > 0) ? 'text-white' : 'text-white/40'
              }`}>
                Preparing Audio
              </span>
            </div>
            {storyComplete && !allAudioComplete && audioTotal > 0 && audioCompleted > 0 && (
              <span className="text-spark-400 text-xs font-bold tabular-nums">
                {Math.min(audioCompleted, audioTotal)}/{audioTotal}
              </span>
            )}
            {allAudioComplete && <span className="text-green-400 text-xs font-bold">✓ Complete</span>}
          </div>
        </div>

        {/* Progress Bar */}
        <div className="space-y-3">
          <div className="relative h-3 w-full bg-white/5 rounded-full overflow-hidden border border-white/10">
            <div 
              className="h-full bg-gradient-to-r from-gold-600 via-forge-500 to-spark-500 transition-all duration-500 ease-out rounded-full"
              style={{ width: `${Math.min(progressPercent, 100)}%` }}
            >
              <div className="absolute inset-0 bg-[linear-gradient(45deg,rgba(255,255,255,0.1)_25%,transparent_25%,transparent_50%,rgba(255,255,255,0.1)_50%,rgba(255,255,255,0.1)_75%,transparent_75%,transparent)] bg-[length:30px_30px] animate-[shimmer_2s_linear_infinite]"></div>
            </div>
          </div>
          
          <div className="flex justify-between text-xs text-gray-400">
            <span className="tabular-nums">{Math.round(progressPercent)}%</span>
            <span className="tabular-nums">Elapsed: {formatTime(elapsedSeconds)}</span>
          </div>
        </div>

        {/* Time Estimate */}
        <p className="text-gray-500 text-sm">
          ⏱ Typically takes 30-60 seconds
        </p>

        {/* iOS: Wake Lock not available — prompt user to keep screen on */}
        {'wakeLock' in navigator ? null : (
          <p className="text-yellow-500/70 text-xs">
            📱 Keep your screen on while your story is being made
          </p>
        )}
      </div>

      <style>{`
        @keyframes shimmer {
          0% { background-position: 0 0; }
          100% { background-position: 60px 0; }
        }
      `}</style>
    </div>
  );
};

export default StoryCreationProgress;